import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StoryOfTheDay from "@/components/StoryOfTheDay";
import { isNative } from "@/lib/native";
import {
  Plus, Image as ImageIcon, House, MapPin, Calendar,
  AddressBook, ArrowRight, Sparkle, WhatsappLogo, Printer, Gift,
  X, Microphone, FilmSlate, SealCheck, Globe, ShieldCheck,
  ShareNetwork, Check, Copy,
} from "@phosphor-icons/react";
import BulkGreetingsModal from "@/components/BulkGreetingsModal";
import HomeAlerts from "@/components/HomeAlerts";
import AIHealthBanner from "@/components/AIHealthBanner";
import VolantinoStimaModal from "@/components/VolantinoStimaModal";
import UsageMiniDashboard from "@/components/UsageMiniDashboard";
// iter v26 — MySharedLinks rimosso da Home (user feedback: distraeva).
import OnboardingMagico from "@/components/OnboardingMagico";
// iter v33 r106 · PrimoSetupWizard rimosso — era un DOPPIONE confermato di
// ProfileSetupWizard (stessi campi: foto, colore, agency_name, telefono).
// Al primo login si sovrapponevano a: SitoIntroModal + ProfileSetupWizard +
// PrimoSetupWizard + OnboardingMagico → catena confusa. Ora unico setup =
// ProfileSetupWizard (skippabile), unico tour = OnboardingMagico (skippabile).
import FeaturedWeeklyCard from "@/components/FeaturedWeeklyCard";
import { useAuth } from "@/lib/auth";
import ProfileSetupWizard, { shouldShowProfileWizard } from "@/components/ProfileSetupWizard";
import { BULK_GREETINGS_LABEL_LONG } from "@/lib/labels";

/**
 * Home — the agent's landing page after login.
 *
 * Replaces the old "drop straight into Annunci list" experience. Shows a
 * compact hub of the 4 main starting points, plus a soft summary of today's
 * agenda when present. Clean, no marketing copy, no fake metrics.
 *
 * Design rules:
 *  - 4 primary action cards, equal weight, large hit area.
 *  - One subtle KPI strip (only when there's real data).
 *  - "Oggi in agenda" only if events exist; never an empty placeholder.
 */
export default function Home() {
  const { user } = useAuth();
  // iter v28 r369 — User M1278: wizard "completa profilo" al primo login.
  // iter v28 r419 — Skip wizard se l'utente è già operativo (annunci/contatti).
  // Decidiamo se aprirlo SOLO dopo aver caricato properties+contacts.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [agenda, setAgenda] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [volantinoOpen, setVolantinoOpen] = useState(false);
  const [acquisizione, setAcquisizione] = useState({ trattative: 0, mkt_pitches: 0, mkt_selected: 0, my_landing: null });
  // iter v28 r361 — User M1270: "metti il potenziale nella homepage".
  // Counter delle stime ricevute dal funnel pubblico /stima/{code}.
  const [stimaStats, setStimaStats] = useState(null);

  useEffect(() => {
    (async () => {
      // iter v33 r23 · User M1499 — Home "sparisce" (VetrinaShareCard e
      // Programma inviti visibili, tutto il resto no).
      // ROOT CAUSE: `Promise.all` di 8 chiamate: se anche UNA sola resta
      // appesa (backend lento perche' il kit-worker sta usando tutte le
      // connessioni Mongo, o WebView iOS pausa fetch in background), tutte
      // le altre non risolvono mai → `loading=true` per sempre → tutte le
      // sezioni gated da `!loading && stats` restano nascoste. Le uniche
      // due sopravvissute sono VetrinaShareCard e Programma inviti perche'
      // renderizzano incondizionate.
      // FIX: ogni chiamata ha un timeout hard di 8s. Se scade, cade sul
      // default e va avanti. La home si mostra SEMPRE entro 8s max.
      const withTimeout = (p, ms, fallback) => Promise.race([
        p,
        new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
      ]);
      try {
        const [props, brief, contactsList, mList, mLanding, mktStats, leadsList, sStats] = await Promise.all([
          withTimeout(api.get("/properties").then(r => r.data).catch(() => []), 8000, []),
          withTimeout(api.get("/agenda/briefing").then(r => r.data).catch(() => null), 8000, null),
          withTimeout(api.get("/contacts").then(r => r.data).catch(() => []), 8000, []),
          withTimeout(api.get("/mandates/list").then(r => r.data).catch(() => null), 8000, null),
          withTimeout(api.get("/mandates/my-landing").then(r => r.data).catch(() => null), 8000, null),
          withTimeout(api.get("/marketplace/stats").then(r => r.data).catch(() => null), 8000, null),
          withTimeout(api.get("/leads").then(r => Array.isArray(r.data) ? r.data : (r.data?.leads || [])).catch(() => []), 8000, []),
          withTimeout(api.get("/stima/stats").then(r => r.data).catch(() => null), 8000, null),
        ]);
        setStimaStats(sStats);
        const real = (props || []).filter(p => !p.is_demo);
        setStats({
          total: real.length,
          online: real.filter(p => p.public_url).length,
          photos: real.reduce((s, p) => s + (p.enhanced_count || 0), 0),
          contacts: (contactsList || []).length,
          properties: real,
          leads: leadsList,
        });
        setBriefing(brief || null);
        setAgenda(brief?.today || []);
        setAcquisizione({
          trattative: (mList?.items || mList || []).length || 0,
          mkt_pitches: mktStats?.sent_total || 0,
          mkt_selected: mktStats?.selected_total || 0,
          my_landing: mLanding?.slug ? { ...mLanding, customized: !!mLanding?.headline } : null,
        });
        // iter v28 r419 — Wizard solo per account davvero nuovo (no annunci,
        // no contatti, account < 7 giorni). Senza questa guardia, anche
        // utenti che hanno già usato l'app vedevano il modal aggressivo.
        const accountState = {
          hasProperties: real.length > 0,
          hasContacts: (contactsList || []).length > 0,
        };
        if (shouldShowProfileWizard(user, accountState)) {
          setTimeout(() => setWizardOpen(true), 800);
        }
      } catch {
        setStats({ total: 0, online: 0, photos: 0 });
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Buongiorno";
    if (h < 18) return "Buon pomeriggio";
    return "Buonasera";
  })();

  const todayLabel = new Date().toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long",
  });

  // iter v28 r461 — User M1380: «sulla home metti Sopralluogo Live come
  // primo (era Crea nuovo annuncio), poi sotto più piccolo Crea nuovo
  // annuncio + Editor foto + Agenda + Contatti». Sopralluogo è il vero
  // valore differenziante di Pronto: l'agente vive in sopralluogo.
  // ACTIONS ora contiene le 4 quick action SECONDARIE (no più sopralluogo,
  // che è promosso a hero). Aggiunto "Crea nuovo annuncio" qui.
  const ACTIONS = [
    {
      to: "/dashboard/property/new",
      icon: Plus,
      title: "Crea annuncio",
      desc: "Titolo, prezzo, zona.",
      testid: "home-action-create",
    },
    {
      to: "/dashboard/foto",
      icon: ImageIcon,
      title: "Editor foto",
      desc: "Carica una foto, te la sistemo io.",
      testid: "home-action-foto",
    },
    // iter v28 r465 — User M1385: «aggiungerei in home anche Editor social
    // dove c'è Editor foto». Aggiunto qui tra Editor foto e Agenda.
    // iter v29 r089 — Naming allineato a sidebar: "Editor foto" → "Foto",
    // "Editor social" → "Social".
    // iter v29 r244 — User M1399: «lascia scritto editor foto editor social».
    // Etichette brevi erano poco chiare in home → reintrodotto "Editor".
    {
      to: "/dashboard/social",
      icon: FilmSlate,
      title: "Editor social",
      desc: "Post, carosello, reel da un click.",
      testid: "home-action-social",
    },
    {
      to: "/dashboard/agenda",
      icon: Calendar,
      title: "Agenda",
      desc: agenda.length > 0
        ? `${agenda.length} oggi · prossimo ${agenda[0].time || ""}`
        : "Visite, scadenze, note vocali.",
      testid: "home-action-agenda",
    },
    {
      to: "/dashboard/contatti",
      icon: AddressBook,
      title: "Contatti",
      desc: stats?.contacts ? `${stats.contacts} clienti · WhatsApp pronto` : "Clienti e cosa cercano.",
      testid: "home-action-contatti",
    },
  ];

  // Annunci moved to a thin secondary row — quick access without
  // dominating the home grid (the agent already has a sidebar entry).
  // iter v29 r222 — User: «sotto a Contatti togli gli annunci, ce l'ho
  // già a sinistra». SECONDARY annunci shortcut rimosso dalla Home.
  // La row resta solo per il bottone "Auguri WhatsApp" se ci sono contatti.

  return (
    <AppShell title="Home" breadcrumb="Home">
      {/* iter v33 r106 · Onboarding pulito:
          - ProfileSetupWizard (facoltativo, sempre skippabile) è renderizzato
            più in basso via shouldShowProfileWizard(user, accountState).
          - OnboardingMagico parte SOLO dopo il completamento/skip del wizard
            (via evento `pronto:start-tour-after-wizard`) oppure via URL
            `?onboarding=1` da "Rivedi il tour" nella sidebar.
          - Rimosso PrimoSetupWizard (era doppione di ProfileSetupWizard).
          - SitoPersonaleIntroModal ora appare solo entrando in /dashboard/profile
            (trigger contestuale in iter v33 r106). */}
      <OnboardingMagico user={user} />
      {/* AcquisizioneTour disabilitato iter 242: duplicava OnboardingMagico e
          conteneva contenuti obsoleti (Mandati / Marketplace) post-pivot. */}
      <header className="mb-5" data-testid="home-header">
        <h1 className="text-[24px] sm:text-[26px] font-semibold text-[#1C1C1A] tracking-tight" data-testid="home-greeting">
          {greeting}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-[12.5px] text-[#9A968B] mt-1 capitalize">
          {todayLabel}
        </p>
      </header>

      {/* iter v29 r089 — Home pulita: i 2 banner "Voice" + "Alerts" sono
          ora più discreti, raggruppati visivamente in cima e con padding
          ridotto. Stessa funzionalità, meno rumore. */}
      <div className="mb-5 space-y-2.5" data-testid="home-banners">
        <HomeVoiceBanner />
        <HomeAlerts />
        <AIHealthBanner />
      </div>

      {/* iter v28 r179 — "Cosa fare oggi" · 3 azioni intelligenti.
          Sostituisce la vecchia attesa: ogni mattina l'agente apre Pronto e
          vede al volo le 3 cose più importanti da fare ORA. Le card vengono
          calcolate dinamicamente sui dati reali (lead nuovi, foto da finire,
          todos scaduti, contatti caldi). Se non c'è nulla da fare → sezione
          nascosta (no placeholder vuoto). */}
      {!loading && stats && (() => {
        const tasks = [];
        // 1) Richieste stima nuove (priority MAX — sono lead caldissimi)
        const newLeads = (stats.leads || []).filter(
          (l) => l.kind === "valuation_request" && (l.status || "new") === "new"
        );
        if (newLeads.length > 0) {
          const first = newLeads[0];
          tasks.push({
            id: "new-leads",
            tone: "alert",
            icon: WhatsappLogo,
            title: newLeads.length === 1
              ? `${first.name?.split(" ")[0] || "Un proprietario"} ti ha chiesto una stima`
              : `${newLeads.length} proprietari ti hanno chiesto una stima`,
            sub: "Chiama o manda WhatsApp · è una richiesta calda",
            to: "/dashboard/richieste-stima",
            cta: newLeads.length === 1 ? "Chiama subito" : "Vedi richieste",
            testid: "todo-new-leads",
          });
        }
        // 2) Todos scaduti (impegni passati su contatti)
        const overdue = briefing?.overdue || [];
        if (overdue.length > 0) {
          tasks.push({
            id: "overdue",
            tone: "warn",
            icon: Calendar,
            title: overdue.length === 1
              ? "Hai un impegno scaduto"
              : `Hai ${overdue.length} impegni scaduti`,
            sub: overdue[0]?.title || overdue[0]?.summary || "Da risistemare in agenda",
            to: "/dashboard/agenda",
            cta: "Vai all'agenda",
            testid: "todo-overdue",
          });
        }
        // 3) Annunci senza foto / senza foto migliorate
        const properties = stats.properties || [];
        const propWithoutPhotos = properties.find(
          (p) => !p.public_url && (p.enhanced_count || 0) < (p.photo_count || 0)
        );
        if (propWithoutPhotos && tasks.length < 3) {
          tasks.push({
            id: "photos-pending",
            tone: "info",
            icon: ImageIcon,
            title: "Foto da finire",
            sub: `${propWithoutPhotos.title || "Un annuncio"} ha foto ancora da migliorare`,
            to: `/dashboard/property/${propWithoutPhotos.id}?tab=foto`,
            cta: "Apri annuncio",
            testid: "todo-photos",
          });
        }
        // 4) Contatti caldi (heat≥7 da non ricontattare da troppo)
        const hotContacts = briefing?.hot_contacts || [];
        if (hotContacts.length > 0 && tasks.length < 3) {
          tasks.push({
            id: "hot-contacts",
            tone: "info",
            icon: AddressBook,
            title: hotContacts.length === 1
              ? "1 cliente caldo da risentire"
              : `${hotContacts.length} clienti caldi`,
            sub: "Hanno mostrato interesse · richiamali prima che si raffreddino",
            to: "/dashboard/contatti",
            cta: "Apri rubrica",
            testid: "todo-hot-contacts",
          });
        }
        // 5) Annunci offline (creati ma mai pubblicati)
        const offlineProps = properties.filter((p) => !p.public_url && p.ai_description);
        if (offlineProps.length > 0 && tasks.length < 3) {
          tasks.push({
            id: "offline-props",
            tone: "info",
            icon: House,
            title: offlineProps.length === 1
              ? "1 annuncio pronto da pubblicare"
              : `${offlineProps.length} annunci pronti da pubblicare`,
            sub: "Hai già descrizione e foto · manca solo metterli online",
            to: `/dashboard/property/${offlineProps[0].id}?tab=condivisione`,
            cta: "Pubblica ora",
            testid: "todo-offline",
          });
        }

        if (tasks.length === 0) return null;

        const TONE_BG = {
          alert: "bg-[#FFF6F4] border-[#F0D5CC] hover:border-[#C24E4E]",
          warn:  "bg-[#FFF8E6] border-[#F2D27A] hover:border-[#8B6914]",
          info:  "bg-white border-[#EAE7DF] hover:border-[#7C8B6E]",
        };
        const TONE_ICON_BG = {
          alert: "bg-[#C24E4E]",
          warn:  "bg-[#C8A66B]",
          info:  "bg-[#7C8B6E]",
        };
        const TONE_OVERLINE = {
          alert: "text-[#C24E4E]",
          warn:  "text-[#8B6914]",
          info:  "text-[#5A6A4D]",
        };

        return (
          <section className="mb-6" data-testid="home-todos">
            <div className="flex items-baseline justify-between mb-2.5">
              <h2 className="text-[15px] font-semibold text-[#1C1C1A] inline-flex items-center gap-1.5">
                <Sparkle size={13} weight="fill" className="text-[#7C8B6E]" /> Cosa fare oggi
              </h2>
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-[#9A968B] font-semibold">
                {tasks.length} {tasks.length === 1 ? "cosa" : "cose"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {tasks.slice(0, 3).map((t) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={t.id}
                    to={t.to}
                    data-testid={t.testid}
                    className={`group relative rounded-xl border-2 p-4 transition-all hover:-translate-y-0.5 ${TONE_BG[t.tone]}`}
                  >
                    <div className="flex items-start gap-2.5 mb-2.5">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${TONE_ICON_BG[t.tone]}`}>
                        <Icon size={15} weight="fill" className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className={`text-[9.5px] uppercase tracking-[0.18em] font-bold ${TONE_OVERLINE[t.tone]}`}>
                          Da fare
                        </div>
                        <div className="text-[13.5px] font-semibold text-[#1C1C1A] mt-0.5 leading-tight line-clamp-2">
                          {t.title}
                        </div>
                      </div>
                    </div>
                    <p className="text-[11.5px] text-[#6B6A65] leading-snug line-clamp-2">
                      {t.sub}
                    </p>
                    <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#1C1C1A] group-hover:gap-1.5 transition-all">
                      {t.cta} <ArrowRight size={11} weight="bold" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* iter v28 r464 — User M1383: «troppa roba in fondo... il contatore
          foto migliorate/online/in gestione lo toglierei». Rimossi:
          • UsageMiniDashboard (Consumi piano Beta — in beta tutto è
            "Illimitato", inutile mostrarlo)
          • KPI strip finale 3 colonne (In gestione/Online/Foto migliorate
            — duplicato dei 4 KPI in alto, peggiora la noise visiva)
          I 4 KPI principali (Annunci/Online ora/Contatti/Stime ricevute)
          restano spostati in FONDO sotto al resto (era in alto). */}

      {/* iter v28 r462 — User M1381: «sposta Trovo proprietari sotto agenda
          contatti» — banner spostato PIÙ IN BASSO (era subito dopo i 4 KPI,
          ora dopo il Recap agenda). L'agente apre Pronto la mattina, prima
          guarda cosa fare oggi + agenda, poi pensa a generare nuovi lead.
          Posizionato qui sotto è anche fuori dal "first scroll" su mobile,
          ma è giusto: serve a chi ha già finito le cose di oggi. */}


      {/* iter v28 r407 — User M1316: rimossi banner ridondanti dalla home
          per ridurre rumore visivo:
            • "Hai 2 annunci attivi · Apri Annunci" (coach banner) → tolto
            • "Carica il logo della tua agenzia" → tolto (upload caricava ma
              il banner restava lì, generava confusione. Logo si fa dal Profilo.)
            • Banner stima in fondo → spostato sopra le 4 stats
      */}

      {/* iter v28 r461 — Hero CTA Sopralluogo Live.
          iter v29 r221 — Nascosto su iOS native: Sopralluogo è già nella
          bottom tab bar centrale → no duplicato.
          iter v29 r225 — User: «chi è dentro l'app sa già cos'è il Sopralluogo,
          niente foto promo qua. Eventualmente la claim va sulla landing pubblica».
          Tornato al layout sobrio originale: solo icona + titolo + sottotitolo.
          iter v34 r02 — User: «da desktop il sopralluogo live è inutile perché
          non possiamo farlo». Nascosto anche su desktop web (lg:hidden): resta
          visibile solo su mobile web e su iOS native (dove è nella tab bar). */}
      <section className="mb-6" data-testid="home-actions">
        {!isNative && (
          <Link
            to="/dashboard/sopralluogo"
            data-testid="home-action-sopralluogo"
            className="lg:hidden group flex items-center gap-4 bg-[#1C1C1A] hover:bg-[#2A2A28] text-white rounded-2xl p-5 transition-all hover:shadow-lg mb-3"
          >
            <div className="w-12 h-12 rounded-xl bg-[#7C8B6E] flex items-center justify-center shrink-0">
              <MapPin size={22} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[20px] sm:text-[22px] leading-tight tracking-tight text-white">
                Sopralluogo Live
              </div>
              <div className="text-[12.5px] text-white/65 mt-0.5">
                Parli, scatti, è già fatto.
              </div>
            </div>
            <ArrowRight size={18} weight="bold" className="text-white/70 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0" />
          </Link>
        )}

        {/* 5 quick action — grid 2 col mobile, 3 tablet, 5 desktop.
            iter v28 r465 — Aggiunto "Editor social" (5ª voce). */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {ACTIONS.map(({ to, icon: Icon, title, desc, testid }) => (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              className="group relative rounded-xl p-3.5 bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] hover:-translate-y-0.5 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-[#F1F4ED] flex items-center justify-center">
                  <Icon size={14} weight="duotone" className="text-[#7C8B6E]" />
                </div>
                <h2 className="text-[13.5px] font-semibold tracking-tight text-[#1C1C1A]">
                  {title}
                </h2>
              </div>
              <p className="text-[11.5px] leading-snug text-[#6B6A65] line-clamp-2">
                {desc}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* iter v26 — UsageMiniDashboard spostato in fondo (sopra Programma inviti)
          + MySharedLinks rimosso (user feedback: distraeva, niente data
          actionable). Niente più stat duplicate qui in alto. */}

      {/* iter v32 r37p — User M1446: «non vedo la vetrina, vedo molto grande
          spingi immobile». La VetrinaShareCard veniva dopo Featured e tante
          altre cose. La sposto SUBITO sotto la riga delle 5 azioni: la prima
          cosa "concreta" è il suo mini-sito personale. */}
      <VetrinaShareCard user={user} />

      {/* Annuncio della settimana — engagement loop, si auto-nasconde se
          l'utente non ha annunci con foto. */}
      {!loading && stats && stats.total > 0 && <FeaturedWeeklyCard />}

      {/* iter v28 r407 — Banner "Carica logo" RIMOSSO dalla home.
          User M1316: «toglimi il logo perché non lo carica o sistema che
          lo carichi correttamente». Il caricamento si fa dal Profilo. */}

      {/* iter v28 r407 — SECONDARY row pulita. "Scarica volantino stima"
          spostato nel banner stima sopra (CTA principale).
          iter v29 r222 — Rimosso shortcut "Annunci" (già in sidebar).
          Resta solo "Auguri WhatsApp" se ci sono contatti. */}
      {stats?.contacts > 0 && (
        <section className="mb-10" data-testid="home-secondary">
          <button
            onClick={() => setBulkOpen(true)}
            data-testid="home-bulk-greetings"
            className="w-full bg-white border border-[#EAE7DF] rounded-lg px-3 py-2.5 flex items-center justify-center gap-2 hover:border-[#7C8B6E] hover:bg-[#F1F4ED] transition-colors"
          >
            <WhatsappLogo size={14} weight="duotone" className="text-[#7C8B6E]" />
            <span className="text-[12.5px] font-medium text-[#1C1C1A]">{BULK_GREETINGS_LABEL_LONG}</span>
          </button>
        </section>
      )}

      {/* iter v28 r407 — Banner stima spostato sopra (subito dopo le 4 stats).
          Era qui in fondo dove non si vedeva mai. */}

      {/* Recap mattutino — agenda + scaduti + caldi (iter 244).
          Si vede solo se c'è qualcosa di concreto da mostrare: todos scaduti,
          todos di oggi, contatti caldi (heat>=7) o drifting (heat>=6 da 10gg).
          Niente placeholder vuoti, niente "Oggi nessuna visita".
          iter v28 r462 — User M1381: «recap giornaliero da mostrare completo
          se lo apro, se no occuperebbe troppo». Adesso il recap è
          COLLAPSED di default: vedi solo i 4 counter + bottone "Apri tutto",
          la lista dei todos appare solo se l'agente clicca espandi. */}
      {!loading && briefing && (briefing.overdue?.length || briefing.today?.length || briefing.hot_contacts?.length || briefing.drifting_contacts?.length) > 0 && (
        <RecapAgendaCard briefing={briefing} />
      )}

      {/* iter v28 r462 — User M1381: «sposta Trovo proprietari sotto agenda
          contatti» — banner spostato GIÙ (era subito dopo i 4 KPI, ora dopo
          il Recap agenda). L'agente apre Pronto la mattina, prima guarda
          cosa fare oggi + agenda, POI pensa a generare nuovi lead. */}
      {stimaStats?.referral_code && (() => {
        const profileReady = !!(user?.agency_name && user?.agency_phone);
        const hasLeads = (stimaStats.leads_this_month || 0) > 0;
        const hasEstimates = (stimaStats.total_estimates_this_month || 0) > 0;
        // iter v29 r222 — User: «se uno ha già creato il volantino stima
        // proprio subito condividi su WhatsApp». Con messaggio precompilato
        // + link online (NON file PDF). publicUrl replica formula di
        // LandingPersonaleTab.jsx (slug nome-cognome se disponibile, altrimenti
        // referral_code).
        const _nameSlug = (user?.name || "")
          .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
          .toLowerCase().trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        const _linkSegment = _nameSlug || stimaStats.referral_code;
        // iter v30 r144 — link via /s/{slug} (alias di /api/share/stima/),
        // più pulito da incollare in WhatsApp.
        const _publicStimaUrl = `${window.location.origin}/api/s/${_linkSegment}`;
        // iter v29 r240 — User M1396: copy aggiornato (no "privacy garantita",
        // sostituito con "Niente nome, niente numero di telefono, 100% anonimo").
        const _waMessage = `Ciao, curioso di sapere quanto vale la tua casa? Te lo dico in 30 secondi. Niente nome, niente numero di telefono, 100% anonimo:\n${_publicStimaUrl}`;
        const _waUrl = `https://wa.me/?text=${encodeURIComponent(_waMessage)}`;
        return (
          <div
            className="mb-6 bg-[#7C8B6E] text-white rounded-2xl p-3 sm:p-4 shadow-[0_10px_30px_-12px_rgba(124,139,110,0.55)]"
            data-testid="home-stima-potential-banner"
          >
            <div className="flex items-center gap-2.5 sm:gap-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white text-[#7C8B6E] flex items-center justify-center shrink-0">
                <Sparkle size={16} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9.5px] uppercase tracking-[0.2em] font-bold text-white/80">Trovo proprietari</div>
                <div className="text-[13px] sm:text-[14.5px] font-semibold leading-tight mt-0.5 truncate" data-testid="home-potential-headline">
                  {!profileReady ? (
                    <>Crea la tua pagina stima · 2 minuti per trovare proprietari</>
                  ) : hasLeads ? (
                    <>
                      <span className="text-[#F1F4ED]">{stimaStats.leads_this_month}</span>
                      {" lead caldi questo mese · "}
                      <span>{stimaStats.total_estimates_this_month}</span>
                      {" stime calcolate"}
                    </>
                  ) : hasEstimates ? (
                    <>
                      <span>{stimaStats.total_estimates_this_month}</span>
                      {" stime ricevute · ancora nessun contatto lasciato"}
                    </>
                  ) : (
                    <>La tua pagina stima è pronta · condividila</>
                  )}
                </div>
                <div className="text-[10.5px] sm:text-[11px] text-white/80 mt-0.5">
                  {!profileReady
                    ? "Completa nome agenzia e telefono → la tua pagina è pubblica"
                    : hasLeads
                      ? `${stimaStats.leads_this_week} ${stimaStats.leads_this_week === 1 ? "nell'ultima settimana" : "negli ultimi 7 giorni"} · apri per gestir${stimaStats.leads_this_week === 1 ? "lo" : "li"}`
                      : 'Mandala su WhatsApp o stampa il volantino — i proprietari ti contattano'}
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-1.5 mt-3">
              {/* iter v29 r222 — User: condividi WhatsApp diretto col link
                  online (NO file PDF). Promosso a CTA primaria quando il
                  profilo è pronto. */}
              {profileReady && (
                <a
                  href={_waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="home-stima-cta-share-wa"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1FB855] text-white px-3 py-2 rounded-md text-[12px] font-semibold transition-colors"
                >
                  <WhatsappLogo size={13} weight="fill" /> Condividi su WhatsApp
                </a>
              )}
              <Link
                to="/dashboard/landing"
                data-testid="home-stima-cta-page"
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white text-[#1C1C1A] hover:bg-[#F1F4ED] px-3 py-2 rounded-md text-[12px] font-semibold transition-colors"
              >
                <Sparkle size={12} weight="fill" /> {!profileReady ? "Imposta pagina stima" : "Invia pagina stima"}
                <ArrowRight size={11} weight="bold" />
              </Link>
              {profileReady && (
                <button
                  type="button"
                  onClick={() => setVolantinoOpen(true)}
                  data-testid="home-stima-cta-volantino"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white/15 hover:bg-white/25 text-white border border-white/30 px-3 py-2 rounded-md text-[12px] font-semibold transition-colors"
                >
                  <Printer size={12} weight="duotone" /> Scarica volantino stima
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* iter v28 r464 — UsageMiniDashboard rimosso. In beta tutto è
          "Illimitato" → mostrare quei numeri non aggiunge valore. */}

      {/* iter v32 r37p — VetrinaShareCard spostata in alto (subito dopo le
          5 azioni). Vedi sopra. */}

      {/* Card "Invita un collega" — viralità referral (iter 244).
          Sempre visibile sotto le card primarie, premio aumentato a 60gg.
          L'agente vede subito il suo bonus se ha già invitato qualcuno. */}
      <ReferralHomeBanner />

      {/* Wrapped annuale — appare quando l'agente ha registrato almeno 1 vendita
          (= ha eliminato un annuncio con motivo "Venduto"). A dicembre si auto-promuove. */}
      <WrappedHomeNudge />

      {/* Storia del giorno — daily share-ready WhatsApp Status / IG Story.
          hideWhenEmpty: if the user has no listings yet, render nothing
          (the 4 CTAs above already nudge them to create the first one). */}
      {!loading && stats && stats.total > 0 && (
        <section className="mb-8" data-testid="home-story">
          <StoryOfTheDay hideWhenEmpty />
        </section>
      )}

      {/* iter v28 r464 — User M1383: i 4 KPI spostati dall'alto QUI in
          fondo, sotto Programma inviti e Storia del giorno. Restano i 4
          principali (Annunci/Online ora/Contatti/Stime ricevute), sono
          comunque utili come riepilogo a fine scroll. */}
      {!loading && stats && (stats.total > 0 || stats.contacts > 0) && (
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6" data-testid="home-stat-strip">
          <Link to="/dashboard/annunci" className="bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 transition-colors group" data-testid="stat-annunci">
            <div className="font-semibold text-[18px] sm:text-[28px] tracking-tight leading-none text-[#1C1C1A] tabular-nums">{stats.total}</div>
            <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mt-1.5 inline-flex items-center gap-1">
              Annunci <ArrowRight size={9} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
          <Link to="/dashboard/annunci" className="bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 transition-colors group" data-testid="stat-online">
            <div className="font-semibold text-[18px] sm:text-[28px] tracking-tight leading-none text-[#7C8B6E] tabular-nums">{stats.online}</div>
            <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mt-1.5">
              Online ora
            </div>
          </Link>
          <Link
            to="/dashboard/contatti"
            className="bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 transition-colors group"
            data-testid="stat-contatti"
            title="Le persone che hai aggiunto in rubrica (clienti, proprietari, lead)"
          >
            <div className="font-semibold text-[18px] sm:text-[28px] tracking-tight leading-none text-[#1C1C1A] tabular-nums">{stats.contacts}</div>
            <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mt-1.5 inline-flex items-center gap-1">
              Contatti <ArrowRight size={9} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
          <Link
            to="/dashboard/richieste-stima"
            className="bg-white border border-[#EAE7DF] hover:border-[#7C8B6E] rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 transition-colors group"
            data-testid="stat-richieste"
            title="Proprietari che hanno lasciato i loro contatti sulla tua pagina stima"
          >
            <div className="font-semibold text-[18px] sm:text-[28px] tracking-tight leading-none text-[#1C1C1A] tabular-nums">{briefing?.valuation_requests_new || 0}</div>
            <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mt-1.5 inline-flex items-center gap-1">
              Stime ricevute <ArrowRight size={9} weight="bold" className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        </section>
      )}

      {/* iter v28 r464 — KPI strip finale (In gestione/Online/Foto migliorate)
          RIMOSSA. Duplicava i 4 KPI sopra + dava "rumore visivo" a fine pagina. */}

      <BulkGreetingsModal open={bulkOpen} onClose={() => setBulkOpen(false)} />
      <VolantinoStimaModal open={volantinoOpen} onClose={() => setVolantinoOpen(false)} />
      {wizardOpen && (
        <ProfileSetupWizard
          user={user}
          onClose={() => setWizardOpen(false)}
          onComplete={() => {
            setWizardOpen(false);
            // Forza reload soft della home per riprendere i dati aggiornati
            // (l'auth context si refresha tramite api interceptor).
            window.location.reload();
          }}
        />
      )}
    </AppShell>
  );
}

// ── Helpers per il recap mattutino + banner referral ─────────────────

// iter v32 r37o · User M1444 — Card "Condividi la tua vetrina" sulla Home.
// iter v32 r37u · User M1453 — REFATTORIZZATA in `SitoPersonaleBanner`:
//   versione XL "Hai un sito tuo" con eyebrow forte "Unico in Italia",
//   URL prominente, share/copy/edit a forma pill, mockup di anteprima.
// 2 stati:
// 1. Slug configurato → banner XL con URL, share/copy/edit + microcopy
//    "Il tuo dominio personale · su Pronto"
// 2. PRO/founder senza slug → CTA "Crea il tuo sito (2 min)"
// Per gli utenti free: niente banner.
function VetrinaShareCard({ user }) {
  const slug = user?.public_slug;
  const plan = (user?.plan || "free").toLowerCase();
  const looksPro = plan === "pro" || plan === "pro_agency" || plan === "studio";
  const [copied, setCopied] = useState(false);

  if (!slug && !looksPro) return null;

  // ── Stato 2: PRO senza slug ────────────────────────────────────────
  if (!slug) {
    return (
      <div
        className="mb-6 relative overflow-hidden rounded-2xl border border-[#7C8B6E]/40 shadow-[0_18px_40px_-20px_rgba(28,28,26,0.35)]"
        style={{ background: "linear-gradient(135deg, #FAFAF7 0%, #F1F4ED 50%, #E5EBD8 100%)" }}
        data-testid="home-vetrina-setup-card"
      >
        {/* Pattern di sfondo sottile */}
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-25 blur-2xl" style={{ background: "#7C8B6E" }} />
        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full opacity-15 blur-2xl" style={{ background: "#C8B886" }} />

        <div className="relative p-5 sm:p-6">
          {/* Eyebrow Unico in Italia */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1C1C1A] text-white text-[9.5px] uppercase tracking-[0.18em] font-semibold mb-3" data-testid="vetrina-unique-badge">
            <SealCheck size={11} weight="fill" />
            Unico in Italia
          </div>
          <h3 className="font-editorial text-[24px] sm:text-[30px] leading-[1.05] tracking-tight text-[#1C1C1A] mb-1.5">
            Hai un sito <span className="italic text-[#5F6B53]">tutto tuo.</span>
          </h3>
          <p className="text-[12.5px] sm:text-[13.5px] text-[#3F3E3A] mb-4 max-w-[28em] leading-relaxed">
            Un dominio personale al tuo nome con bio, contatti e tutti i tuoi immobili aggiornati in automatico.
            Da mettere nella bio Instagram, in firma email, sui biglietti da visita.
          </p>
          <div className="flex flex-wrap gap-2 items-center text-[11px] text-[#5F6B53] mb-5">
            <span className="inline-flex items-center gap-1.5"><Globe size={12} weight="fill" /> 2 minuti per crearlo</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5"><Sparkle size={12} weight="fill" /> 4 stili editoriali</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} weight="fill" /> Incluso in Pro</span>
          </div>
          <Link
            to="/dashboard/vetrina-agente"
            data-testid="home-vetrina-setup-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#1C1C1A] hover:bg-[#000] text-white px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all hover:scale-[1.02] active:scale-95"
          >
            Crea il tuo sito <ArrowRight size={13} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  // ── Stato 1: slug configurato ──────────────────────────────────────
  const origin = typeof window !== "undefined"
    ? window.location.origin
    : "https://prontoimmo.it";
  const liveUrl = `${origin}/agente/${slug}`;             // SPA — usato per "Apri" / Modifica
  const displayUrl = `prontoimmo.it/agente/${slug}`;       // testo mostrato nella card
  // iter v32 r49e — User M1452: «orribile, voglio l'anteprima e un solo link».
  // 1. Lo share-friendly URL `/api/share/agente/{slug}` serve l'HTML con OG
  //    meta (logo agente + nome + claim) ai bot WhatsApp/iMessage/Telegram
  //    e fa 302 redirect a `/agente/{slug}` per gli umani. Stesso pattern
  //    usato nel resto del codebase (vedi VetrinaAgente.jsx).
  // 2. Il vecchio messaggio diceva "Ti lascio" (tu) — è un testo destinato
  //    al cliente: deve essere "Le lascio" (formale).
  const shareUrl = `${origin}/api/share/agente/${slug}`;
  const waMessage = `Le lascio il mio sito personale, ci sono tutti i miei immobili:\n${shareUrl}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

  const onCopy = async (e) => {
    e?.preventDefault?.();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const onShare = async (e) => {
    e?.preventDefault?.();
    if (navigator.share) {
      try {
        // iter v32 r49e — Passiamo SOLO `text` (che contiene già l'URL).
        // Prima passavamo anche `url: liveUrl` → WhatsApp/iMessage iOS
        // concatenavano text + url producendo lo stesso link 2 volte.
        await navigator.share({
          title: `Il sito personale di ${user?.name || "agente"} · su Pronto`,
          text: waMessage,
        });
        return;
      } catch { /* utente ha annullato */ }
    }
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="mb-6 relative overflow-hidden rounded-2xl border border-[#7C8B6E]/40 shadow-[0_18px_40px_-20px_rgba(28,28,26,0.35)]"
      style={{ background: "linear-gradient(135deg, #FAFAF7 0%, #F1F4ED 50%, #E5EBD8 100%)" }}
      data-testid="home-vetrina-share-card"
    >
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-25 blur-2xl" style={{ background: "#7C8B6E" }} />
      <div className="relative p-5 sm:p-6">
        {/* Header con stato + eyebrow */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1C1C1A] text-white text-[9.5px] uppercase tracking-[0.18em] font-semibold">
            <SealCheck size={11} weight="fill" />
            Il tuo sito è online
          </div>
          <Link
            to="/dashboard/vetrina-agente"
            data-testid="home-vetrina-edit-btn"
            className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#5F6B53] hover:text-[#1C1C1A] transition-colors"
          >
            Modifica →
          </Link>
        </div>

        {/* URL prominente */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5F6B53] mb-1">
            Dominio personale
          </div>
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-[18px] sm:text-[22px] font-mono leading-tight text-[#1C1C1A] hover:text-[#5F6B53] transition-colors truncate"
            data-testid="home-vetrina-share-url"
          >
            {displayUrl}
          </a>
        </div>

        {/* Azioni pill */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onShare}
            data-testid="home-vetrina-share-btn"
            className="inline-flex items-center gap-1.5 bg-[#1C1C1A] hover:bg-[#000] text-white px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all hover:scale-[1.02] active:scale-95"
          >
            <ShareNetwork size={13} weight="bold" /> Condividi
          </button>
          <button
            type="button"
            onClick={onCopy}
            data-testid="home-vetrina-copy-btn"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] border border-[#1C1C1A]/15 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all"
          >
            {copied ? <><Check size={13} weight="bold" /> Copiato</> : <><Copy size={13} weight="regular" /> Copia link</>}
          </button>
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] border border-[#1C1C1A]/15 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all"
            data-testid="home-vetrina-open-btn"
          >
            <Globe size={13} weight="regular" /> Apri
          </a>
        </div>
      </div>
    </div>
  );
}

// iter v28 r462 — User M1381: «recap giornaliero da mostrare COMPLETO se
// lo apro, se no occuperebbe troppo». Wrapper collapsable: counter sempre
// visibili, lista todos collassata di default. Persistito lo stato in
// localStorage così se l'agente lo lascia aperto/chiuso resta tale al
// ricaricamento.
function RecapAgendaCard({ briefing }) {
  const STORAGE_KEY = "pronto:home:recap-expanded";
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; }
    catch { return false; }
  });
  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const overdueLen = briefing.overdue?.length || 0;
  const todayLen = briefing.today?.length || 0;
  const totalTodos = overdueLen + todayLen;
  const hasList = totalTodos > 0;

  return (
    <section className="mb-8" data-testid="home-recap">
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-[#1C1C1A] inline-flex items-center gap-1.5">
            <Sparkle size={13} weight="fill" className="text-[#7C8B6E]" /> Il tuo recap di oggi
          </h3>
          <p className="text-[11px] text-[#9A968B] mt-0.5">Tutto quello che hai sull{"'"}agenda, in un colpo d{"'"}occhio.</p>
        </div>
        <Link to="/dashboard/agenda" className="text-[11.5px] text-[#7C8B6E] hover:text-[#1C1C1A] inline-flex items-center gap-1 font-medium shrink-0" data-testid="home-recap-open">
          Apri agenda <ArrowRight size={11} weight="bold" />
        </Link>
      </div>

      {/* Counter strip — sempre visibili */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="home-recap-counters">
        <RecapStat label="Scaduti" value={overdueLen} accent={overdueLen > 0} tone="warn" />
        <RecapStat label="Oggi" value={todayLen} accent={todayLen > 0} />
        <RecapStat label="Caldi" value={briefing.hot_contacts?.length || 0} />
        <RecapStat label="Da risentire" value={briefing.drifting_contacts?.length || 0} />
      </div>

      {hasList && (
        <>
          {/* Toggle: collassato di default — l'agente clicca per espandere */}
          <button
            type="button"
            onClick={toggle}
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#F1F4ED] hover:bg-[#E5EAD9] text-[#1C1C1A] py-2 rounded-md text-[12px] font-semibold transition-colors mb-1.5"
            data-testid="home-recap-toggle"
          >
            {expanded ? "Nascondi dettagli" : `Mostra ${totalTodos} ${totalTodos === 1 ? "impegno" : "impegni"}`}
            <ArrowRight
              size={11}
              weight="bold"
              className={`transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </button>

          {expanded && (
            <div className="space-y-1.5" data-testid="home-recap-list">
              {(briefing.overdue || []).slice(0, 3).map((t) => (
                <RecapTodoRow key={t.id} todo={t} overdue />
              ))}
              {(briefing.today || []).slice(0, 5 - Math.min(3, overdueLen)).map((t) => (
                <RecapTodoRow key={t.id} todo={t} />
              ))}
              {totalTodos > 5 && (
                <Link to="/dashboard/agenda" className="block text-center text-[11.5px] text-[#7C8B6E] hover:text-[#1C1C1A] py-1.5 font-medium">
                  + altre {totalTodos - 5} cose in agenda
                </Link>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RecapStat({ label, value, accent = false, tone = "default" }) {
  const ring = value > 0 && tone === "warn"
    ? "border-[#C24E4E]/30 bg-[#FFF6F4]"
    : accent
    ? "border-[#7C8B6E] bg-[#F1F4ED]"
    : "border-[#EAE7DF] bg-white";
  const valueColor = value > 0 && tone === "warn"
    ? "text-[#C24E4E]"
    : accent
    ? "text-[#7C8B6E]"
    : "text-[#1C1C1A]";
  return (
    <div className={`rounded-lg border ${ring} px-3 py-2`} data-testid={`home-recap-stat-${label.toLowerCase()}`}>
      <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-[#6B6A65]">{label}</div>
      <div className={`text-[20px] font-semibold tabular-nums leading-tight mt-0.5 ${valueColor}`}>{value}</div>
    </div>
  );
}

function RecapTodoRow({ todo, overdue = false }) {
  const time = (() => {
    if (!todo.due_at) return "—";
    try {
      const d = new Date(todo.due_at);
      return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
  })();
  return (
    <Link
      to="/dashboard/agenda"
      className={`flex items-center gap-3 rounded-lg px-3.5 py-2.5 border transition-colors ${
        overdue
          ? "bg-[#FFF6F4] border-[#F0D5CC] hover:border-[#C24E4E]/50"
          : "bg-white border-[#EAE7DF] hover:border-[#7C8B6E]"
      }`}
      data-testid={`home-recap-row-${todo.id}`}
    >
      <div className={`text-[12px] font-mono tabular-nums font-semibold w-12 shrink-0 ${overdue ? "text-[#C24E4E]" : "text-[#7C8B6E]"}`}>
        {time}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-[#1C1C1A] truncate font-medium">{todo.title || todo.summary || "Senza titolo"}</div>
        {overdue && <div className="text-[10.5px] text-[#C24E4E] mt-0.5 font-medium uppercase tracking-[0.14em]">Scaduto</div>}
      </div>
      <ArrowRight size={12} weight="bold" className="text-[#9A968B] shrink-0" />
    </Link>
  );
}

function WrappedHomeNudge() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/wrapped/availability").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);
  if (!info || !info.available_years || info.available_years.length === 0) return null;
  const year = info.auto_show_year || info.available_years[0];
  const isAuto = !!info.auto_show_year;
  return (
    <Link
      to={`/dashboard/wrapped/${year}`}
      className={`block mb-8 rounded-2xl overflow-hidden border transition-all hover:-translate-y-0.5 ${
        isAuto
          ? "border-[#C8A66B] bg-gradient-to-br from-[#7C8B6E] to-[#6A7860] text-white shadow-lg hover:shadow-xl"
          : "border-[#EAE7DF] bg-white hover:border-[#7C8B6E]"
      }`}
      data-testid="home-wrapped-nudge"
    >
      <div className="p-5 sm:p-6 flex items-center gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
          isAuto ? "bg-[#C8A66B]" : "bg-[#F1F4ED]"
        }`}>
          <Sparkle size={22} weight="fill" className={isAuto ? "text-[#7C8B6E]" : "text-[#7C8B6E]"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-1 ${
            isAuto ? "text-[#C8A66B]" : "text-[#7C8B6E]"
          }`}>
            {isAuto ? `È arrivato dicembre! · WRAPPED ${year}` : `Wrapped ${year}`}
          </div>
          <h3 className={`text-[15.5px] font-semibold tracking-tight ${isAuto ? "" : "text-[#1C1C1A]"}`}>
            {isAuto ? `Il tuo ${year} con Pronto è pronto.` : `Guarda il tuo report ${year}.`}
          </h3>
          <p className={`text-[12px] mt-0.5 leading-relaxed ${isAuto ? "text-white/75" : "text-[#6B6A65]"}`}>
            Case vendute, volume transato, commissioni stimate. Pronto per Instagram e LinkedIn.
          </p>
        </div>
        <ArrowRight size={16} weight="bold" className={`shrink-0 ${isAuto ? "text-[#C8A66B]" : "text-[#9CA3AF]"}`} />
      </div>
    </Link>
  );
}

function ReferralHomeBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return null;
  const link = data.link || "";
  const converted = data.invites_converted || 0;
  // iter 309 r15: copy aggiornata ai crediti (no più "30 giorni Pro").
  const creditsEarned = data.credits_earned_total || 0;
  const rewardReferrer = data.reward_credits_per_conversion || 30;
  const rewardInvitee = data.reward_credits_invitee_welcome || 30;
  const waText = encodeURIComponent(
    `Ciao! Volevo passarti Pronto. È un'app che uso per gestire annunci, foto, social e contatti — fa risparmiare un sacco di tempo, soprattutto dopo i sopralluoghi. Iscriviti col mio link e ti regalano ${rewardInvitee} crediti per iniziare: ${link}`
  );
  return (
    <section
      className="mb-8 rounded-xl overflow-hidden border border-[#EAE7DF] bg-[#FAF9F4]"
      data-testid="home-referral-banner"
    >
      <div className="p-4 sm:p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="inline-flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#C8A66B] mb-1">
              <Gift size={9} weight="fill" /> Programma inviti
            </div>
            <h3 className="text-[13.5px] sm:text-[14px] leading-snug text-[#1C1C1A] font-semibold">
              Invita un collega → <span className="text-[#7C8B6E]">{rewardReferrer} crediti gratuiti</span> per te.
            </h3>
            {converted > 0 && (
              <p className="text-[11px] text-[#6B6A65] mt-1">
                Già {converted} {converted === 1 ? "conversione" : "conversioni"} · {creditsEarned} crediti guadagnati.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={`https://wa.me/?text=${waText}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366] text-white text-[12px] font-semibold hover:opacity-90 transition-opacity"
              data-testid="home-referral-wa"
            >
              <WhatsappLogo size={12} weight="fill" /> Invia
            </a>
            <Link
              to="/dashboard/referrals"
              className="inline-flex items-center gap-1 text-[#1C1C1A] text-[12px] font-medium hover:text-[#7C8B6E] transition-colors"
              data-testid="home-referral-page"
            >
              Dettagli <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// iter v28 r463 — Banner home "Parla con Pronto" (option b).
// Persistente in cima alla home finché:
//   • l'utente clicca X manualmente, oppure
//   • ha cliccato il mic FAB almeno 5 volte (counter `pronto:mic-fab:clicks`)
// Re-letto da localStorage ad ogni mount. Niente fetch backend.
function HomeVoiceBanner() {
  // iter v29 r045 — User: «il pulsante microfono parla con pronto in
  // homepage all'inizio lo farei sparire». Banner disabilitato.
  // Manteniamo il componente vivo per non rompere import, ma ritorna null.
  return null;
}

// iter v29 r234 — Disabled component prefixed with underscore + funzione
// rinominata in `HomeVoiceBannerDisabled` (uppercase) per ESLint
// rules-of-hooks. Era un componente disattivato lasciato in repo per
// possibile riattivazione futura.
// eslint-disable-next-line no-unused-vars
function HomeVoiceBannerDisabled() {
  const DISMISS_KEY = "pronto:home-voice-banner:dismissed";
  const MIC_CLICKS_KEY = "pronto:mic-fab:clicks";
  const MIC_CLICKS_THRESHOLD = 5;

  const [visible, setVisible] = useState(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") return false;
      const clicks = parseInt(localStorage.getItem(MIC_CLICKS_KEY) || "0", 10);
      if (clicks >= MIC_CLICKS_THRESHOLD) return false;
      return true;
    } catch {
      return true;
    }
  });

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  // Apri il mic FAB usando l'evento globale che GlobalMicFab già ascolta
  // (vedi useEffect("pronto:open-voice-fab") in App.js)
  const openMic = () => {
    try { window.dispatchEvent(new CustomEvent("pronto:open-voice-fab")); }
    catch { /* ignore */ }
  };

  return (
    <div
      className="mb-5 relative bg-gradient-to-r from-[#1C1C1A] via-[#2A2A28] to-[#1C1C1A] text-white rounded-2xl px-4 py-3.5 sm:px-5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.45)] overflow-hidden"
      data-testid="home-voice-banner"
    >
      {/* Decorative pulse circle dietro */}
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#7C8B6E]/20 blur-2xl pointer-events-none" aria-hidden />

      <div className="flex items-start sm:items-center gap-3 sm:gap-4">
        <button
          onClick={openMic}
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#7C8B6E] hover:bg-[#6B7A5D] flex items-center justify-center shrink-0 transition-colors active:scale-95"
          aria-label="Apri Parla con Pronto"
          data-testid="home-voice-banner-mic"
        >
          <Microphone size={20} weight="fill" className="text-white" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-[#C8B886]">
            Parla con Pronto
          </div>
          <div className="text-[13.5px] sm:text-[15px] font-semibold leading-snug mt-0.5">
            Tu parli, io scrivo. <span className="text-white/65 font-normal">Promemoria che finiscono in agenda, annunci, note, brief — tutto da voce.</span>
          </div>
          <button
            type="button"
            onClick={openMic}
            className="inline-flex items-center gap-1 mt-1.5 text-[11.5px] text-[#C8B886] hover:text-white font-semibold transition-colors"
            data-testid="home-voice-banner-cta"
          >
            Prova adesso <ArrowRight size={11} weight="bold" />
          </button>
        </div>
        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white shrink-0 -mr-1 p-1.5 rounded-md transition-colors"
          aria-label="Chiudi banner"
          data-testid="home-voice-banner-dismiss"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

